/**
 * ThumbnailUploadButton — library-card control to upload a custom
 * thumbnail (JPG/PNG) for any story. Replaces the auto-extracted frame
 * everywhere: gallery, story page, RSS artwork, share cards.
 */
import { useRef, useState } from "react";
import axios from "axios";
import { Image, Loader2, Sparkles, RotateCcw } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ThumbnailUploadButton({ videoId, title, onUploaded }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [confirmAI, setConfirmAI] = useState(false);
  const [confirmUp, setConfirmUp] = useState(false);
  const [confirmReset, setConfirmReset] = useState(false);

  const resetToFrame = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/videos/${videoId}/thumbnail/reset`, {}, { timeout: 180_000 });
      toast.success(title ? `Thumbnail reset to video frame for "${title}"` : "Thumbnail reset to video frame");
      onUploaded?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Thumbnail reset failed");
    } finally {
      setBusy(false);
    }
  };

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    toast.info("Uploading thumbnail…");
    try {
      const fd = new FormData();
      fd.append("file", file);
      await axios.post(`${API}/videos/${videoId}/thumbnail`, fd, {
        timeout: 120_000,
      });
      toast.success(title ? `Thumbnail updated for "${title}"` : "Thumbnail updated");
      onUploaded?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Thumbnail upload failed");
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const generate = async () => {
    if (!window.confirm(`Generate an AI thumbnail for "${title || "this story"}" from its title and category? It replaces the current image (you can always upload your own after).`)) return;
    setBusy(true);
    try {
      await axios.post(`${API}/videos/${videoId}/thumbnail/generate`, {}, { timeout: 120_000 });
      toast.success(title ? `AI thumbnail created for "${title}"` : "AI thumbnail created");
      onUploaded?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI thumbnail failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,.jpg,.jpeg,.png,.webp"
        className="hidden"
        onChange={(e) => upload(e.target.files?.[0])}
      />
      <button
        onClick={() => {
          // No window.confirm here: it eats the user activation and
          // Chrome then blocks the programmatic file-input click, so
          // the picker never opened. Two-step inline confirm instead.
          if (busy) return;
          if (!confirmUp) {
            setConfirmUp(true);
            setTimeout(() => setConfirmUp(false), 5000);
            return;
          }
          setConfirmUp(false);
          inputRef.current?.click();
        }}
        disabled={busy}
        className={`text-[11px] uppercase tracking-[0.18em] py-2 rounded-lg border transition-colors flex items-center justify-center gap-1.5 disabled:opacity-40 ${confirmUp ? "border-amber-400 text-amber-300" : "border-white/10 hover:border-[color:var(--accent)] hover:text-[color:var(--accent)]"}`}
        data-testid={`library-thumbnail-${videoId}`}
        title="Upload a custom thumbnail picture for this story (JPG or PNG)"
      >
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Image className="w-3.5 h-3.5" />}
        {confirmUp ? "Choose file?" : "Thumbnail"}
      </button>
      <button
        onClick={() => {
          if (busy) return;
          if (!confirmAI) {
            setConfirmAI(true);
            setTimeout(() => setConfirmAI(false), 5000);
            return;
          }
          setConfirmAI(false);
          generate();
        }}
        disabled={busy}
        className={`text-[11px] uppercase tracking-[0.18em] py-2 rounded-lg border transition-colors flex items-center justify-center gap-1.5 disabled:opacity-40 ${confirmAI ? "border-amber-400 text-amber-300" : "border-white/10 hover:border-[color:var(--accent)] hover:text-[color:var(--accent)]"}`}
        data-testid={`library-ai-thumbnail-${videoId}`}
        title="Let AI create a cinematic thumbnail from the story's title and category — replaces the current image"
      >
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
        {busy ? "Working…" : confirmAI ? "Replace image?" : "AI Thumb"}
      </button>
      <button
        onClick={() => {
          if (busy) return;
          if (!confirmReset) {
            setConfirmReset(true);
            setTimeout(() => setConfirmReset(false), 5000);
            return;
          }
          setConfirmReset(false);
          resetToFrame();
        }}
        disabled={busy}
        className={`text-[11px] uppercase tracking-[0.18em] py-2 rounded-lg border transition-colors flex items-center justify-center gap-1.5 disabled:opacity-40 ${confirmReset ? "border-amber-400 text-amber-300" : "border-white/10 hover:border-[color:var(--accent)] hover:text-[color:var(--accent)]"}`}
        data-testid={`library-reset-thumbnail-${videoId}`}
        title="Reset the thumbnail back to the standard auto-extracted video frame (undoes AI/custom thumbnails so it matches the rest of the library)"
      >
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />}
        {confirmReset ? "Use video frame?" : "Frame"}
      </button>
    </>
  );
}
